import repeat from "common/utils/repeat";
import type { BBox } from "rbush-3d";
import { RBush3D } from "rbush-3d";
import type { Face } from "../brep";
import { faceAABB3 } from "../brep";
import { intersectFacePlane } from "../intersect";
import { IntersectionKind } from "../intersectRay2";
import type { Segment } from "./dcel";
import { dcelFromPolygons, oppositeHalfEdgeIndex } from "./dcelFromPolygons";
import { setOpByPredicatePolygons } from "./setOps";
import { faceToPolygon, polygonToFace } from "./trimFaceWithPlane";

export enum HalfEdgeClassification {
  NON_INTERACTING = "NON_INTERACTING",
  FRONT = "FRONT",
  BACK = "BACK",
}

export enum FaceClassification {
  KEEP = "KEEP",
  DISCARD = "DISCARD",
}

interface DCELAndClassifications {
  dcel: ReturnType<typeof dcelFromPolygons>;
  halfEdgeClassifications: HalfEdgeClassification[];
  faceClassifications: FaceClassification[];
  segments: Segment[];
}

/**
 * Computes the DCEL and edge classifications for a face being trimmed by cut faces.
 * This helper function extracts the core logic of building the DCEL and classifying edges.
 */
export function buildFaceTrimmingClassifications(
  face: Face,
  cutFaces: Face[],
  cutFacesTree?: RBush3D,
): DCELAndClassifications {
  const bb = faceAABB3(face);

  if (!cutFacesTree) {
    const bbs = cutFaces.map((x, faceIdx) => ({ ...faceAABB3(x), faceIdx }));
    cutFacesTree = new RBush3D(bbs.length);
    cutFacesTree.load(bbs);
  }

  const res = cutFacesTree.search(bb) as (BBox & { faceIdx })[];

  const isectSegments: Segment[] = [];
  const errs = [];
  for (const { faceIdx } of res) {
    const isect = intersectFacePlane(cutFaces[faceIdx], face.plane, errs);
    if (isect && isect.kind === IntersectionKind.TRANSVERSE) {
      isectSegments.push(...isect.segments.map((x) => [x.planeU0, x.planeV0, x.planeU1, x.planeV1] as Segment));
    }
  }

  const CLIP_SEGMENT_ID = 1;

  // form a dcel using the face and segments, classify the segments
  const dcel = dcelFromPolygons({
    polygons: [faceToPolygon(face)],
    segments: isectSegments,
    segmentPolygonIds: repeat(isectSegments.length, CLIP_SEGMENT_ID),
  });

  // classify the half edges by whether they are on the front back or non interacting with the cutting segments
  const halfEdgeClassifications = dcel.halfEdges.map((_, i) => {
    const polys = dcel.halfEdgeToPolygon[i];

    if (polys.includes(CLIP_SEGMENT_ID)) {
      return HalfEdgeClassification.FRONT;
    }

    const oppPolys = dcel.halfEdgeToPolygon[oppositeHalfEdgeIndex(i)];
    if (oppPolys.includes(CLIP_SEGMENT_ID)) {
      return HalfEdgeClassification.BACK;
    }

    return HalfEdgeClassification.NON_INTERACTING;
  });

  // determine if the face is clipped by crossing the cut faces
  const isClippedFace = (faceId) => {
    const face = dcel.faceHalfEdges[faceId];

    let backCount = 0;
    let nonInteractingCount = 0;

    for (const halfEdgeIdx of face) {
      const classification = halfEdgeClassifications[halfEdgeIdx];
      if (classification === HalfEdgeClassification.FRONT) {
        return false;
      } else if (classification === HalfEdgeClassification.BACK) {
        backCount++;
      } else {
        nonInteractingCount++;
      }
    }

    if (dcel.faceIsInterior(faceId)) {
      return nonInteractingCount > 0;
    }

    return backCount > 0 && nonInteractingCount > 0;
  };

  const getFaceClassification = (faceId: number) => {
    if (!dcel.faceToPolygons[faceId]?.has(0)) {
      return FaceClassification.DISCARD;
    }

    if (isClippedFace(faceId)) {
      return FaceClassification.KEEP;
    }

    // the face is a child face that may or may not be part of the result.
    // it is classified based on its parent face.

    if (!dcel.faceIsInterior(faceId)) {
      const parent = dcel.faceToParentFace[faceId];

      if (parent !== undefined) {
        return getFaceClassification(parent);
      }
    }

    return FaceClassification.DISCARD;
  };

  const faceClassifications = dcel.faceHalfEdges.map((_, i) => getFaceClassification(i));

  return { dcel, halfEdgeClassifications, faceClassifications, segments: isectSegments };
}

/**
 * Trim a face by removing all of the parts of it in front of the cut faces. The "front" of the plane is defined
 * as the side towards which the plane normal points.
 *
 * This algorithm proceeds by converting the face and the clipping plane into a planar 2d problem constituted
 * by a DCEL. The edges are classified as being part of the intersection or not and if the intersection segment is
 * front or back of cut. The other edgs are classified as non-interacting. The DCEL is then traversed to find the
 * faces that should be kept. The faces are then converted back to 3d.
 */
export function trimFaceWithFaces(
  face: Face,
  cutFaces: Face[],
  cutFacesTree?: RBush3D,
  remainderFaces?: Face[],
): { kind: IntersectionKind; faces: Face[] } {
  const { dcel, faceClassifications } = buildFaceTrimmingClassifications(face, cutFaces, cutFacesTree);

  // combine all of the faces that are classified to the front
  const polys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
    const face = dcel.halfEdgeToFace[halfEdgeIndex];
    return faceClassifications[face] === FaceClassification.KEEP;
  });

  // If remainder output is requested, extract DISCARD faces belonging to the original polygon
  if (remainderFaces) {
    const remainderPolys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];
      return faceClassifications[face] === FaceClassification.DISCARD && (dcel.faceToPolygons[face]?.has(0) ?? false);
    });
    for (const poly of remainderPolys) {
      remainderFaces.push(polygonToFace(poly, face.plane));
    }
  }

  return { kind: IntersectionKind.TRANSVERSE, faces: polys.map((poly) => polygonToFace(poly, face.plane)) };
}

/**
 * Split a face by cut faces, returning both the matched (KEEP) faces and the remainder faces.
 * This is similar to trimFaceWithFaces, but also returns the parts of the original face that
 * were not matched by the cut faces.
 */
export function splitFaceWithFaces(
  face: Face,
  cutFaces: Face[],
  cutFacesTree?: RBush3D,
): { kind: IntersectionKind; faces: Face[] } {
  const remainderFaces: Face[] = [];
  const { kind, faces } = trimFaceWithFaces(face, cutFaces, cutFacesTree, remainderFaces);
  return { kind, faces: [...faces, ...remainderFaces] };
}
